import { useState } from 'react';
import { useCuttingStore } from '../state/useCuttingStore';
import { DEFAULT_UNIFORM_SHEET } from '../model/defaults';
import type { SheetSizeConfig } from '../model/types';

/**
 * Block 14 «Раскрой» — модалка «Размер листа» (PR #6a-PARTS, §5.1.9).
 * Пиксельный порт `project.html:912-1005` (sheet-size-modal) + `setSheetMode`/
 * `applySheetSize`/`pick*SheetSize` (1082-1216). Два режима (радио): одинаковые
 * размеры (uniform) / разные размеры (mixed, 2 фиксированных варианта).
 *
 * Связка со стором: ОК → updateSettings({ sheetConfig }) → dirty (data.sheetConfig
 * зеркалит settings.sheetConfig, projectSync §5.1.9). Текст кнопок — чёрный.
 */

/** Пресеты размеров (project.html:941-949, вербатим). */
const PRESETS: ReadonlyArray<[number, number]> = [
  [2440, 1830],
  [2620, 1830],
  [2750, 1830],
  [2750, 1750],
  [2800, 2070],
  [2800, 1220],
  [3050, 1830],
  [3060, 1830],
  [3500, 1830],
];

// Единый источник UI-дефолта (PR #23): тот же литерал использует buildEngineInput-fallback.
const DEFAULT_UNIFORM: [number, number] = [
  DEFAULT_UNIFORM_SHEET.length,
  DEFAULT_UNIFORM_SHEET.width,
];
const DEFAULT_V1: [number, number] = [2750, 1830];
const DEFAULT_V2: [number, number] = [2440, 1220];

function toInt(v: string, fallback: number): number {
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? fallback : n;
}

export function SheetSizeDialog({ onClose }: { onClose: () => void }) {
  const sheetConfig = useCuttingStore((s) => s.document.settings.sheetConfig);
  const updateSettings = useCuttingStore((s) => s.updateSettings);

  const [mode, setMode] = useState<'uniform' | 'mixed'>(sheetConfig.mode);

  const [uw, setUw] = useState(sheetConfig.uniformSheet?.length ?? DEFAULT_UNIFORM[0]);
  const [uh, setUh] = useState(sheetConfig.uniformSheet?.width ?? DEFAULT_UNIFORM[1]);

  const mv = sheetConfig.mixedVariants;
  const [w1, setW1] = useState(mv?.[0].length ?? DEFAULT_V1[0]);
  const [h1, setH1] = useState(mv?.[0].width ?? DEFAULT_V1[1]);
  const [w2, setW2] = useState(mv?.[1].length ?? DEFAULT_V2[0]);
  const [h2, setH2] = useState(mv?.[1].width ?? DEFAULT_V2[1]);

  const [openDropdown, setOpenDropdown] = useState<null | 'uniform' | 'v1' | 'v2'>(null);

  function applyPreset(target: 'uniform' | 'v1' | 'v2', l: number, w: number) {
    if (target === 'uniform') {
      setUw(l);
      setUh(w);
    } else if (target === 'v1') {
      setW1(l);
      setH1(w);
    } else {
      setW2(l);
      setH2(w);
    }
    setOpenDropdown(null);
  }

  function apply() {
    if (mode === 'uniform') {
      const next: SheetSizeConfig = { mode: 'uniform', uniformSheet: { length: uw, width: uh } };
      updateSettings({ sheetConfig: next });
    } else {
      if (w1 === w2 && h1 === h2) {
        // project.html:1148-1150 — оба варианта обязаны отличаться
        window.alert(
          'В режиме «Листы разных размеров» оба варианта должны отличаться. Иначе используйте режим «Листы одинаковых размеров».',
        );
        return;
      }
      const next: SheetSizeConfig = {
        mode: 'mixed',
        mixedVariants: [
          { length: w1, width: h1 },
          { length: w2, width: h2 },
        ],
      };
      updateSettings({ sheetConfig: next });
    }
    onClose();
  }

  return (
    <div
      className="sc-modal2-overlay sheet-size-modal visible"
      role="dialog"
      aria-modal="true"
      aria-label="Размер листа"
    >
      <div className="sc-modal2">
        <div className="sc-modal2-title">
          <span>📐 Размер листа</span>
          <button type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="sc-modal2-content" style={{ position: 'relative' }}>
          {/* радио-группа режима */}
          <div className="sheet-mode-group">
            <label className="sheet-mode-option">
              <input
                type="radio"
                name="sheet-mode"
                value="uniform"
                checked={mode === 'uniform'}
                onChange={() => {
                  setMode('uniform');
                  setOpenDropdown(null);
                }}
              />
              <span>Листы одинаковых размеров</span>
            </label>
            <label className="sheet-mode-option">
              <input
                type="radio"
                name="sheet-mode"
                value="mixed"
                checked={mode === 'mixed'}
                onChange={() => {
                  setMode('mixed');
                  setOpenDropdown(null);
                }}
              />
              <span>Листы разных размеров (программа подберёт оптимальную комбинацию)</span>
            </label>
          </div>

          {/* Режим: одинаковые размеры */}
          {mode === 'uniform' && (
            <div className="sheet-mode-section">
              <div className="sheet-size-input-row">
                <input
                  type="number"
                  className="sc-input kb-input"
                  aria-label="Длина листа"
                  value={uw}
                  onChange={(e) => setUw(toInt(e.target.value, DEFAULT_UNIFORM[0]))}
                />
                <span>×</span>
                <input
                  type="number"
                  className="sc-input kb-input"
                  aria-label="Ширина листа"
                  value={uh}
                  onChange={(e) => setUh(toInt(e.target.value, DEFAULT_UNIFORM[1]))}
                />
                <button
                  type="button"
                  className="rt-btn"
                  aria-label="Выбрать размер из списка"
                  onClick={() => setOpenDropdown((d) => (d === 'uniform' ? null : 'uniform'))}
                >
                  ▾
                </button>
              </div>
              {openDropdown === 'uniform' && (
                <div className="sheet-size-dropdown-list visible">
                  {PRESETS.map(([l, w]) => (
                    <button
                      key={`${l}x${w}`}
                      type="button"
                      onClick={() => applyPreset('uniform', l, w)}
                    >
                      {l} × {w}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Режим: разные размеры (2 варианта) */}
          {mode === 'mixed' && (
            <div className="sheet-mode-section">
              <div className="mixed-variant-row">
                <span className="mixed-variant-label">Вариант 1</span>
                <input
                  type="number"
                  className="sc-input kb-input"
                  aria-label="Вариант 1 — длина"
                  value={w1}
                  onChange={(e) => setW1(toInt(e.target.value, DEFAULT_V1[0]))}
                />
                <span>×</span>
                <input
                  type="number"
                  className="sc-input kb-input"
                  aria-label="Вариант 1 — ширина"
                  value={h1}
                  onChange={(e) => setH1(toInt(e.target.value, DEFAULT_V1[1]))}
                />
                <button
                  type="button"
                  className="rt-btn mixed-dropdown-btn"
                  aria-label="Вариант 1 — выбрать размер из списка"
                  onClick={() => setOpenDropdown((d) => (d === 'v1' ? null : 'v1'))}
                >
                  ▾
                </button>
              </div>
              {openDropdown === 'v1' && (
                <div className="sheet-size-dropdown-list visible">
                  {PRESETS.map(([l, w]) => (
                    <button
                      key={`v1-${l}x${w}`}
                      type="button"
                      onClick={() => applyPreset('v1', l, w)}
                    >
                      {l} × {w}
                    </button>
                  ))}
                </div>
              )}

              <div className="mixed-variant-row">
                <span className="mixed-variant-label">Вариант 2</span>
                <input
                  type="number"
                  className="sc-input kb-input"
                  aria-label="Вариант 2 — длина"
                  value={w2}
                  onChange={(e) => setW2(toInt(e.target.value, DEFAULT_V2[0]))}
                />
                <span>×</span>
                <input
                  type="number"
                  className="sc-input kb-input"
                  aria-label="Вариант 2 — ширина"
                  value={h2}
                  onChange={(e) => setH2(toInt(e.target.value, DEFAULT_V2[1]))}
                />
                <button
                  type="button"
                  className="rt-btn mixed-dropdown-btn"
                  aria-label="Вариант 2 — выбрать размер из списка"
                  onClick={() => setOpenDropdown((d) => (d === 'v2' ? null : 'v2'))}
                >
                  ▾
                </button>
              </div>
              {openDropdown === 'v2' && (
                <div className="sheet-size-dropdown-list visible">
                  {PRESETS.map(([l, w]) => (
                    <button
                      key={`v2-${l}x${w}`}
                      type="button"
                      onClick={() => applyPreset('v2', l, w)}
                    >
                      {l} × {w}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="sc-modal2-footer">
          <button type="button" className="sc-modal-btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="sc-modal-btn primary" onClick={apply}>
            ОК
          </button>
        </div>
      </div>
    </div>
  );
}
